import {
    DashboardSchema
} from '@/contexts/AdminContext/dashboardProvider';
import type { Layout, LayoutItem } from 'react-grid-layout';
import { GridItem, GroupedLayout } from '../types';

export const buildGroupedLayouts = (sc: DashboardSchema, cols: number = 12): GroupedLayout[] => {
    const groups: GroupedLayout[] = [];
    console.log('buildGroupedLayouts', sc);

    // 如果没有分组，创建一个默认隐藏分组
    if (!sc.group || sc.group.length === 0) {
        const allItems: GridItem[] = [];
        // 收集所有 KPI
        (sc.kpi || []).forEach((k, idx) => {
            const key = k.id || `kpi-${idx}`;
            allItems.push({
                key,
                type: 'kpi',
                data: k
            });
        });
        // 收集所有 Chart
        (sc.chart || []).forEach((c, idx) => {
            const key = c.id || `chart-${idx}`;
            allItems.push({
                key,
                type: 'chart',
                data: c
            });
        });

        // 如果有 items，创建默认分组
        if (allItems.length > 0) {
            const defaultLayout = computeCompactLayout(allItems, cols);
            return [{
                group: {
                    id: 'default',
                    title: 'Default',
                    items_id: allItems.map(item => item.key)
                },
                items: allItems,
                layout: defaultLayout as unknown as Layout,
                hidden: true // 标记为隐藏分组
            }];
        }
        return [];
    }

    // 创建 ID 到 item 的映射
    const itemMap = new Map<string, GridItem>();
    (sc.kpi || []).forEach((k, idx) => {
        const key = k.id || `kpi-${idx}`;
        itemMap.set(key, {
            key,
            type: 'kpi',
            data: k
        });
    });
    (sc.chart || []).forEach((c, idx) => {
        const key = c.id || `chart-${idx}`;
        itemMap.set(key, {
            key,
            type: 'chart',
            data: c
        });
    });

    // 收集已分配的 items
    const assignedKeys = new Set<string>();

    // 为每个分组构建布局
    if (Array.isArray(sc.group)) {
        sc.group.forEach((groupData) => {
            const groupItems: GridItem[] = [];

            // 收集该分组中的所有 items
            (groupData.items_id || []).forEach((itemId) => {
                const item = itemMap.get(itemId);
                if (item) {
                    groupItems.push(item);
                    assignedKeys.add(itemId);
                }
            });

            // 如果分组中有 items，添加到结果中
            if (groupItems.length > 0) {
                // 为该分组的 items 重新计算紧凑布局
                const groupLayoutItems = computeCompactLayout(groupItems, cols);

                groups.push({
                    group: groupData,
                    items: groupItems,
                    layout: groupLayoutItems as unknown as Layout
                });
            }
        });
    }

    return groups;
};

/**
 * 为一组 items 计算紧凑布局（向左向上对齐）
 */
const computeCompactLayout = (items: GridItem[], cols: number): LayoutItem[] => {
    const layoutItems: LayoutItem[] = [];
    const occupiedPositions = new Set<string>();

    items.forEach(item => {
        // 确定默认宽度和高度
        const itemLayout = item.data.layout;
        const defaultW = item.type === 'kpi' ? 3 : 6;
        const defaultH = item.type === 'kpi' ? 2 : 6;

        const w = itemLayout?.w ?? defaultW;
        const h = itemLayout?.h ?? defaultH;

        // 如果 schema 中已经保存了位置信息，直接使用
        if (itemLayout && itemLayout.x !== undefined && itemLayout.y !== undefined) {
            // 使用存储的位置，但要检查是否超出列数
            const clampedX = Math.min(itemLayout.x, cols - w);
            const clampedY = itemLayout.y;

            // 标记该位置已被占用
            for (let dy = 0; dy < h; dy++) {
                for (let dx = 0; dx < w; dx++) {
                    occupiedPositions.add(`${clampedX + dx},${clampedY + dy}`);
                }
            }

            layoutItems.push({
                i: item.key,
                x: clampedX,
                y: clampedY,
                w,
                h,
                minW: 2,
                maxW: 12
            });
            return;
        }

        // 没有存储的位置信息，需要自动计算
        // 寻找第一个可用位置（从左上角开始）
        let targetX = 0;
        let targetY = 0;
        let foundPosition = false;

        while (!foundPosition) {
            // 在当前行中搜索可用位置
            while (targetX + w <= cols) {
                // 检查这个位置是否可用
                let canPlace = true;
                for (let dy = 0; dy < h && canPlace; dy++) {
                    for (let dx = 0; dx < w && canPlace; dx++) {
                        if (occupiedPositions.has(`${targetX + dx},${targetY + dy}`)) {
                            canPlace = false;
                            targetX += dx + 1; // 跳过被占用的位置
                            break;
                        }
                    }
                }

                if (canPlace) {
                    foundPosition = true;
                    break;
                }
            }

            if (!foundPosition) {
                // 当前行没有可用位置，换到下一行
                targetX = 0;
                targetY++;
            }
        }

        // 标记新占用的位置
        for (let dy = 0; dy < h; dy++) {
            for (let dx = 0; dx < w; dx++) {
                occupiedPositions.add(`${targetX + dx},${targetY + dy}`);
            }
        }

        layoutItems.push({
            i: item.key,
            x: targetX,
            y: targetY,
            w,
            h,
            minW: 2,
            maxW: 12
        });
    });

    return layoutItems;
};
